import RequestedFeature from '@carvana/merch-contracts/RequestedFeature';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useMemo } from 'react';
import { MAX_PAGE_SIZE_DEFAULT } from '~/constants/srp';
import { useIsLoggedIn } from '~/context/common/UserContext';
import SuggestedFiltersContext from '~/context/srp/SuggestedFiltersContext';
import useGenerateAnalyticsData from '~/hooks/srp/useGenerateAnalyticsData';
import useGenerateInventoryRequest from '~/hooks/srp/useGenerateInventoryRequest';
import useSuggestedFiltersConsolidationExperiment from '~/hooks/srp/useSuggestedFiltersConsolidationExperiment';
import fetchSuggestedFilters from '~/services/common/fetchSuggestedFilters';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetSuggestedFilters(): void {
  const {
    setSuggestedFiltersRequestId,
    suggestedFilters: { setData, setError, setLoaded, setLoading }
  } = useContext(SuggestedFiltersContext);

  const isLoggedIn = useIsLoggedIn();
  const { shouldConsolidate } = useSuggestedFiltersConsolidationExperiment();
  const generateAnalyticsData = useGenerateAnalyticsData();
  const { request, conditionalRequestedFeatures } = useGenerateInventoryRequest(
    MAX_PAGE_SIZE_DEFAULT,
    {
      includePageNumber: true,
      limitRequestedFeatures: !isLoggedIn
    }
  );

  const requestedFeatures = useMemo(
    () =>
      request.requestedFeatures
        .concat(...conditionalRequestedFeatures)
        .concat(
          shouldConsolidate
            ? [RequestedFeature.ConsolidatedSuggestedFilters]
            : []
        ),
    [conditionalRequestedFeatures, request.requestedFeatures, shouldConsolidate]
  );

  useEffectWithController(
    controller => {
      if (request.dealershipId) return;

      setLoading();

      fetchSuggestedFilters(
        {
          ...request,
          analyticsData: generateAnalyticsData(),
          requestedFeatures,
          numberOfSuggestedFilters: 10
        },
        controller
      )
        .then(({ data }) => {
          setData(data.suggestedFilters);
          setSuggestedFiltersRequestId(data.suggestedFiltersRequestId);
        })
        .catch(
          generateErrorLogger(
            'Suggested filters',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      generateAnalyticsData,
      request,
      requestedFeatures,
      setData,
      setError,
      setLoaded,
      setLoading,
      setSuggestedFiltersRequestId
    ]
  );
}
